import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Button as MuiButton,
} from "@mui/material";
import { Alert, Button, Typography } from "@platform/design-system";
import { Link } from "react-router";

import { RFP_STATUSES, isValidRFPStatus } from "../../constants/rfp";
import { extractErrorMessage } from "../../utils";

import { useConsultingRfpTimelineQuery } from "./useConsultingRfpTimeline";

import type { ConsultingRfpTimelineItem } from "./types";

const UNKNOWN_CLIENT_LABEL = "Unknown client";

export function ConsultingRfpTimeline({
  brokerId,
  showBrokerName,
  enabled,
}: {
  brokerId?: string;
  showBrokerName: boolean;
  enabled: boolean;
}): React.JSX.Element {
  const timelineQuery = useConsultingRfpTimelineQuery({ brokerId, enabled });
  const items = timelineQuery.data?.pages.flatMap((page) => page.results) ?? [];
  const emptyCopy = brokerId
    ? "No submitted RFPs found for this broker."
    : "No submitted RFPs found for your brokers.";

  return (
    <Paper
      data-testid="consulting-rfp-timeline"
      variant="outlined"
      sx={{ mt: 3, p: 2 }}
    >
      <Stack spacing={2}>
        <Typography variant="display-small">Recently Submitted RFPs</Typography>

        {timelineQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : null}

        {timelineQuery.isError ? (
          <Alert
            severity="error"
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => timelineQuery.refetch()}
              >
                Retry
              </Button>
            }
          >
            {extractErrorMessage(timelineQuery.error)}
          </Alert>
        ) : null}

        {!timelineQuery.isLoading &&
        !timelineQuery.isError &&
        items.length === 0 ? (
          <Typography color="textSecondary">{emptyCopy}</Typography>
        ) : null}

        {items.length > 0 ? (
          <Stack divider={<Divider flexItem />}>
            {items.map((item) => (
              <RfpTimelineRow
                key={item.id}
                item={item}
                showBrokerName={showBrokerName}
              />
            ))}
          </Stack>
        ) : null}

        {timelineQuery.hasNextPage ? (
          <Box>
            <Button
              variant="secondary"
              onClick={() => timelineQuery.fetchNextPage()}
              disabled={timelineQuery.isFetchingNextPage}
            >
              Load more
            </Button>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

function RfpTimelineRow({
  item,
  showBrokerName,
}: {
  item: ConsultingRfpTimelineItem;
  showBrokerName: boolean;
}): React.JSX.Element {
  return (
    <Stack
      data-testid="rfp-timeline-row"
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      alignItems={{ xs: "flex-start", md: "center" }}
      justifyContent="space-between"
      py={1.5}
    >
      <Stack spacing={0.5} minWidth={0}>
        <Typography variant="body-medium" color="textSecondary">
          {formatDisplayDate(item.submittedDate)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1" color="textSecondary">
            {item.clientName ?? UNKNOWN_CLIENT_LABEL}
          </Typography>
          <Typography variant="body-medium" color="textSecondary">
            RFP #{item.id}
          </Typography>
          <Chip size="small" label={statusLabel(item.status)} />
        </Stack>
        {showBrokerName ? (
          <Typography variant="body-medium" color="textSecondary">
            {item.brokerName}
          </Typography>
        ) : null}
        <Typography variant="body-medium" color="textSecondary">
          Due {formatOptionalDisplayDate(item.dueDate)} · Effective{" "}
          {formatOptionalDisplayDate(item.effectiveDate)}
        </Typography>
      </Stack>
      {/* TODO(ds-button): link-button — DS Button type isn't polymorphic (component/to) */}
      <MuiButton
        component={Link}
        to={`/rfps/${item.id}`}
        variant="text"
        size="small"
      >
        View RFP
      </MuiButton>
    </Stack>
  );
}

function statusLabel(status: string): string {
  return isValidRFPStatus(status) ? RFP_STATUSES[status] : status;
}

function formatOptionalDisplayDate(value: string | null): string {
  return value ? formatDisplayDate(value) : "—";
}

function formatDisplayDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === new Date().getFullYear()
      ? {}
      : { year: "numeric" }),
  };

  return new Intl.DateTimeFormat("en-US", options).format(date);
}
